import React, {useEffect, useState} from 'react';
import { useFormContext } from '../Provider';
import useValidators from '../Validators/useValidator';
import CommonFieldWrapper from './CommonFieldWrapper';
import Icon from '../../Icons/Icon';
import Help from './Fragments/Help';
import { plural } from '../../../Services/helpers';

let debounce = null;
let checkedPasswords = {};

const Password = ({label, name, help, onChange, showPrompts, showHasLeaked, placeholder, disabled, validators}) => {
    const { formData, updateField, updateValidity, isValidating, isSubmitting, formDisabled } = useFormContext();
    const value = formData[name] ?? '';
    const {isValid, error} = useValidators(value, validators, name, updateValidity);
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const [valueDebounced, setValueDebounced] = useState(value);
    const [hasLeaked, setHasLeaked] = useState(null);

    const handleChange = (e) => {
        updateField(name, e.target.value);
        onChange && onChange(e.target.value);
    }

    useEffect(() => {
        if (debounce) {
            clearTimeout(debounce);
        }
        setHasLeaked(null);
        debounce = setTimeout(() => {
            setValueDebounced(value);
        }, 500);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    useEffect(() => {
        if (showHasLeaked) {
            checkIfPasswordLeaked(valueDebounced).then(setHasLeaked);
        }
    }, [valueDebounced, showHasLeaked]);

    const isDisabled = formDisabled || disabled || isSubmitting;

    return <CommonFieldWrapper {...{label, name, help, value, isValid, isValidating, error, validators, isDisabled}}>

        <div className="relative flex flex-row">
            <input className="block w-full border-0 p-0 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6 disabled:text-gray-400 disabled:bg-gray-100"
                type="password"
                value={value}
                onChange={handleChange}
                placeholder={placeholder}
                disabled={isDisabled}
            />

            <div className="cursor-pointer inset-y-0 right-0 flex items-center pl-1" onClick={() => setIsPasswordVisible(!isPasswordVisible)}>
                <span className="fill-gray-900 sm:text-sm">
                    <Icon icon={isPasswordVisible ? 'Eye' : 'EyeSlash'} />
                </span>
            </div>
        </div>

        {isPasswordVisible && <Help help={value} />}

        {(showPrompts && value) && <Prompts password={value} showHasLeaked={showHasLeaked} hasLeaked={hasLeaked} />}
        
    </CommonFieldWrapper>
}

const Prompts = ({password, showHasLeaked, hasLeaked}) => {
    return <p className="mt-1">
        {[
            prompt(/[a-z]/.test(password), 'Lower case letter'),
            prompt(/[A-Z]/.test(password), 'Upper case letter'),
            prompt(/\d/.test(password), 'Number character'),
            prompt(/[^a-zA-Z0-9]/.test(password), 'Special symbol'),
            prompt(password.length >= 8, '8 Characters or more'),
            showHasLeaked ? <HasLeaked hasLeaked={hasLeaked} /> : null,
        ]}
    </p>
}

const HasLeaked = ({hasLeaked}) => {
    
    if (hasLeaked === null) {
        return <span key="pw" className="block text-xs m-0 text-gray-400">
            <Icon icon="Circle" size="0.5x" inline className="mr-2 fill-gray-400" /> Checking if leaked...
        </span>
    }

    if (hasLeaked === false) {
        return prompt(true, 'Password not found in any data leaks');
    }

    if (hasLeaked > 0) {
        return <span key="pw" className="block text-xs m-0 text-gray-400 flex flex-row">
            <Icon icon="TriangleExclamation" size="1x" inline className="mr-2 fill-red-400" />
            <span className="inline-block">
                <strong className="text-red-400">
                    Password found in {hasLeaked.toLocaleString()} data {plural(hasLeaked, 'leaks', 'leak', 'leaks')} - you should not use this password.
                </strong><br />
                If you use this password in other places you should change it immediately.<br />
                <a href="https://haveibeenpwned.com/Passwords" target="_blank" className="link" rel="noreferrer">Learn more at haveibeenpwned.com</a>
            </span>
        </span>
    }
}

const prompt = (b, m) => {
    return <span key={m} className={'block text-xs m-0 text-gray-400'}>
        <Icon icon={b ? 'Check' : 'XMark'} size="0.5x" inline className={'mr-2 '+(b ? 'fill-green-500' : 'fill-red-400')} />
        {m}
    </span>
}

const checkIfPasswordLeaked = async (password) => {
    if (checkedPasswords[password] !== undefined) {
        return checkedPasswords[password];
    }

    try {
        // Convert the password string into an ArrayBuffer
        const encoder = new TextEncoder();
        const data = encoder.encode(password);
  
        // Hash the password using the SHA-1 algorithm
        const hashBuffer = await crypto.subtle.digest('SHA-1', data);
        const hashArray = Array.from(new Uint8Array(hashBuffer)); // Convert buffer to byte array
        const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join(''); // Convert bytes to hex string
        const search = hashHex.substring(0, 5);
        const url = `https://api.pwnedpasswords.com/range/${search}`;
  
        // Fetch the data from the pwnedpasswords API
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`Request Failed. Status Code: ${response.status}`);
        }
        const content = await response.text();
  
        // Search for the password hash suffix in the response
        const match = hashHex.substring(5).toUpperCase();
        const regex = new RegExp(`${match}:([0-9]+)`);
        const matches = content.match(regex);
  
        // Return the count if found, otherwise null
        const result = matches ? parseInt(matches[1], 10) : false;
        checkedPasswords[password] = result;
        return result;
    } catch (error) {
        console.error('An error occurred:', error);
        return null;
    }
}

export default Password;